import * as I18n from 'circe/I18n';
import moment from 'moment';
import { fetch } from 'domain/operations/drivers/itineraryStatus/manifest/api';
import { STOP_TYPES } from 'domain/trip/constants';
import {
  BAD_VOLUME_UTILIZATION,
  BAD_WEIGHT_UTILIZATION,
  GOOD_VOLUME_UTILIZATION,
  GOOD_WEIGHT_UTILIZATION,
} from 'domain/trip/constants';

export const calVolumeUtilization = (value) => getUtilization(value, 'Volume');
export const calWeightUtilization = (value) => getUtilization(value, 'Weight');

export const getUtilization = (utilizationValue, flag) => {
  const badThreshold = { Volume: BAD_VOLUME_UTILIZATION, Weight: BAD_WEIGHT_UTILIZATION };
  const goodThreshold = { Volume: GOOD_VOLUME_UTILIZATION, Weight: GOOD_WEIGHT_UTILIZATION };

  const utilizationFunc = (utilizationValue, flag) => {
    if (utilizationValue <= badThreshold[flag]) {
      return {
        value: `bad${flag}Utilization`,
        title: I18n.str(
          `bad`,
          'Bad -> Under utilized, this trip is losing {diff}% {flag}, minimum 40%',
          {
            flag: I18n.str(flag, flag),
            diff: (badThreshold[flag] - utilizationValue).toFixed(2),
          },
        ),
      };
    } else if (utilizationValue > badThreshold[flag] && utilizationValue <= goodThreshold[flag]) {
      return {
        value: `good${flag}Utilization`,
        title: I18n.str(`good`, 'Good -> {flag} within range, target >80%', {
          flag: I18n.str(flag, flag),
        }),
      };
    } else {
      return {
        value: `best${flag}Utilization`,
        title: I18n.str(
          `great`,
          'Great -> above target, this trip is saving {diff}% {flag}, target >80%',
          {
            flag: I18n.str(flag, flag),
            diff: (utilizationValue - goodThreshold[flag]).toFixed(2),
          },
        ),
      };
    }
  };

  try {
    return utilizationFunc(utilizationValue, flag);
  } catch (e) {
    console.error(e);
    return I18n.str('n/a', 'N/A');
  }
};

export const formatDate = (date) => (date ? moment(date).format('llll') : null);
export const getIcons = (color) => {
  return {
    pickup: `https://raw.githubusercontent.com/Concept211/Google-Maps-Markers/master/images/marker_${color}P.png`,
    return: `https://raw.githubusercontent.com/Concept211/Google-Maps-Markers/master/images/marker_${color}R.png`,
    stop: `https://raw.githubusercontent.com/Concept211/Google-Maps-Markers/master/images/marker_${color}__index__.png`,
    trip: `https://raw.githubusercontent.com/Concept211/Google-Maps-Markers/master/images/marker_${color}T.png`,
  };
};
export const formatMultipleItinerariesMap = async (itineraries) => {
  //// red, black, blue, green, grey, orange, purple, white, yellow

  const colors = ['red', 'blue', 'orange', 'black', 'purple'];
  let index = 0;
  let allStops = [];
  let coded = {};
  let itinerariesData = [];
  let itinerariesCodes = [];
  let itinerariesIds = [];
  let itinerariesShipments = [];
  for (const itinerary of itineraries) {
    const color = colors[index];
    const results = await fetch(itinerary);
    const itineraryId = results.summary.id;
    const itineraryCode = results.summary.itineraryCode;
    const shipments = results.shipments;
    const icons = getIcons(color);

    const itineraryStops = results?.summary?.stops.map((stop, i) => {
      const { stopType } = stop;
      if (stopType === STOP_TYPES.PICKUP) {
        return { ...stop, style: { icon: icons.pickup } };
      } else if (stopType === STOP_TYPES.RETURN) {
        return { ...stop, style: { icon: icons.return } };
      } else {
        return { ...stop, style: { icon: icons.stop.replace('__index__', i) } };
      }
    });
    itinerariesIds = [...itinerariesIds, itineraryId];
    itinerariesCodes = [...itinerariesCodes, itineraryCode];
    itinerariesData = [...itinerariesData, results];
    itinerariesShipments = [...itinerariesShipments, ...shipments];
    coded = {
      ...coded,
      [color]: {
        stops: itineraryStops,
        summary: results.summary,
        icon: icons.trip,
      },
    };
    allStops = [...allStops, ...itineraryStops];
    index++;
  }
  return {
    coded,
    allStops,
    itinerariesData,
    itinerariesIds,
    itinerariesCodes,
    itinerariesShipments,
  };
};

export const getTripLabel = (trip) => {
  const {
    summary: { summaryCode, itineraryCode, id },
  } = trip;
  if (summaryCode) {
    return `${summaryCode} , ${itineraryCode}`;
  }
  if (itineraryCode) return itineraryCode;
  return id;
};

export const DateOnFilterToUTC = (value) => {
  return value?.map((value) => {
    return moment(value)
      .utc()
      .toISOString();
  });
};

export const getLoadSummary = (items) =>
  items.reduce((summary, item) => {
    const { quantity } = item;
    const { mass } = item;
    const { length, width, height } = item.dimensions;
    const volume = length * width * height;

    const { type } = item;

    if (summary[type]) {
      summary[type].totalQuantity += quantity;
      summary[type].totalMass += mass;
      summary[type].totalVolume += volume;
    } else {
      summary[type] = {
        totalQuantity: quantity,
        totalMass: mass,
        totalVolume: volume,
      };
    }

    return summary;
  }, {});

export const toReadableNumber = (number) => {
  // This utility will convert the number to the first non-zero number.
  if (number <= 0) {
    return number;
  }

  let i = 1;
  while (number < 10) {
    number *= 10;
    i *= 10;
  }
  return parseInt(number) / i;
};
export const startDateOnFilter = (value) => {
  return value?.map((value, index) => {
    if (index == 0)
      return moment(value)
        .startOf('day')
        .toISOString();
    else
      return moment(value)
        .endOf('day')
        .toISOString();
  });
};
export const chunkArray = (arr, chunkSize) => {
  const R = [];
  for (let i = 0, len = arr.length; i < len; i += chunkSize) R.push(arr.slice(i, i + chunkSize));
  return R;
};
export const filterVehicleTypes = (vehicleTypes) =>
  vehicleTypes
    .filter(({ type }) => Boolean(type))
    .map(({ type, label }) => ({
      value: type,
      label: I18n.str(type, label),
    }));
